// import once and as early as possible
import 'reflect-metadata';

import Vue from 'vue';
import StoryblokVue from 'storyblok-vue';
import JsonRPC from 'simple-jsonrpc-js';
import { container } from 'tsyringe';

import lax from './plugins/lax';
import router from './router';
import { messenger } from './messenger';
import { ModelManager } from './prototyping/model';

// include global styles
import './style/index.scss';

declare global {
  interface Window {
    // eslint-disable-next-line
    jrpc: any; // TODO let this interact more natively with the Typescript Ecosystem by using custom types (probably pull request simple-jsonrpc-js)
    initUnity: () => void;
  }
}

// A dependency graph that contains any wasm must all be imported
// asynchronously. This file does the single async import, so
// that no one else needs to worry about it again.

Vue.config.productionTip = false;
Vue.use(lax);
Vue.use(StoryblokVue);

async function timeout(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

window.jrpc = new JsonRPC();

async function initApp() {
  const App = await import('./App.vue');
  const modelManager = container.resolve(ModelManager);
  let it = modelManager.get('image-target-tracks', 'StoryblokImageTargetTracksLibModel');
  let al = modelManager.get('audio-lib', 'StoryblokAudioLibModel');
  while (it.state.imageTargetTracks.length === 0 || al.state.audioLib.length === 0) {
    it = modelManager.get('image-target-tracks', 'StoryblokImageTargetTracksLibModel');
    al = modelManager.get('audio-lib', 'StoryblokAudioLibModel');
    /* eslint-disable no-await-in-loop, @typescript-eslint/no-var-requires */
    await timeout(10);
  }

  new Vue({
    router,
    render: (h) => h(App.default),
  }).$mount('#app');
}

window.initUnity = async () => {
  messenger.setup();
  // eslint-disable-next-line
  console.log('Initialize Unity <-> WebView Communication');
  // eslint-disable-next-line
  const toStream = window.jrpc.toStream;
  // eslint-disable-next-line
  const messageHandler = window.jrpc.messageHandler;

  const appState = localStorage.getItem('serializedAppState');
  if (appState) container.register('serializedAppState', { useValue: appState });

  const modelManager = container.resolve(ModelManager);
  modelManager.get('image-target-tracks', 'StoryblokImageTargetTracksLibModel');
  modelManager.get('audio-lib', 'StoryblokAudioLibModel');
  const globalState = modelManager.global;

  // overwrite the jrpc functions 'toStream' and 'messageHandler' to log the serialized messages
  window.jrpc.toStream = (m: string) => {
    globalState.logUnityRPCMessage(m);
    toStream(m);
  };

  window.jrpc.messageHandler = (m: string) => {
    globalState.logWebViewRPCMessage(m);
    messageHandler(m);
  };

  await initApp();

  messenger.unity?.webview.openExternalUrlsInBrowser();
};

(async () => {
  if (!navigator.userAgent.includes('Mangalica')) {
    window.initUnity();
  }
})();
